import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lightning } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function NewMotorcyclePage() {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const [formData, setFormData] = useState({
    chassis_sn: "",
    model: "",
    voltage: 60,
    store_id: "",
    technician_id: "",
    checklist_template_id: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    // Filter technicians by selected store
    if (formData.store_id) {
      fetchTechnicians(formData.store_id);
    } else {
      fetchTechnicians();
    }
  }, [formData.store_id]);

  const fetchOptions = async () => {
    try {
      const [storesRes, techsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/stores`, { withCredentials: true }),
        axios.get(`${API}/technicians`, { withCredentials: true }),
        axios.get(`${API}/checklist-templates`, { withCredentials: true }),
      ]);
      setStores(storesRes.data);
      setTechnicians(techsRes.data);
      setChecklistTemplates(templatesRes.data);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const fetchTechnicians = async (storeId = null) => {
    try {
      const url = storeId
        ? `${API}/technicians?store_id=${storeId}`
        : `${API}/technicians`;
      const response = await axios.get(url, { withCredentials: true });
      setTechnicians(response.data);
    } catch (error) {
      console.error("Error fetching technicians:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        chassis_sn: formData.chassis_sn.trim().toUpperCase(),
        model: formData.model.trim(),
        voltage: parseInt(formData.voltage),
        store_id: formData.store_id || null,
        technician_id: formData.technician_id || null,
        checklist_template_id: formData.checklist_template_id || null,
      };

      const response = await axios.post(`${API}/motorcycles`, data, {
        withCredentials: true,
      });

      toast.success("Moto cadastrada com sucesso!");
      navigate(`/motos/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao cadastrar moto";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Nova Moto" showBack backTo="/dashboard">
      <form
        onSubmit={handleSubmit}
        className="space-y-6 pb-4"
        data-testid="new-moto-form"
      >
        {/* Chassis SN */}
        <div className="space-y-2">
          <label className="text-sm text-[#A1A1AA] ml-1">
            Número do Chassi (SN)
          </label>
          <input
            type="text"
            value={formData.chassis_sn}
            onChange={(e) =>
              setFormData({
                ...formData,
                chassis_sn: e.target.value.toUpperCase(),
              })
            }
            className="input-field font-mono uppercase w-full"
            placeholder="9BWZZZ377VT004251"
            required
            data-testid="chassis-sn-input"
          />
          <p className="text-[11px] sm:text-xs text-[#A1A1AA] ml-1">
            Número único de identificação do chassi
          </p>
        </div>

        {/* Model */}
        <div className="space-y-2">
          <label className="text-sm text-[#A1A1AA] ml-1">Modelo</label>
          <input
            type="text"
            value={formData.model}
            onChange={(e) =>
              setFormData({ ...formData, model: e.target.value })
            }
            className="input-field w-full"
            placeholder="Voltz EVS"
            required
            data-testid="model-input"
          />
        </div>

        {/* Voltage */}
        <div className="space-y-2">
          <label className="text-sm text-[#A1A1AA] ml-1">Voltagem</label>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, voltage: 60 })}
              className={`h-24 sm:h-28 rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                formData.voltage === 60
                  ? "border-[#00E676] bg-[#00E676]/10 shadow-[0_0_15px_rgba(0,230,118,0.15)]"
                  : "border-white/10 bg-[#121212] hover:bg-[#1A1A1A]"
              }`}
              data-testid="voltage-60-button"
            >
              <Lightning
                size={28}
                weight="fill"
                className={`sm:w-8 sm:h-8 ${formData.voltage === 60 ? "text-[#00E676]" : "text-[#A1A1AA]"}`}
              />
              <span
                className={`text-lg sm:text-xl font-bold ${formData.voltage === 60 ? "text-[#00E676]" : "text-white"}`}
              >
                60V
              </span>
              <span className="text-[11px] sm:text-xs text-[#A1A1AA]">
                5 baterias
              </span>
            </button>

            <button
              type="button"
              onClick={() => setFormData({ ...formData, voltage: 72 })}
              className={`h-24 sm:h-28 rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                formData.voltage === 72
                  ? "border-[#00E676] bg-[#00E676]/10 shadow-[0_0_15px_rgba(0,230,118,0.15)]"
                  : "border-white/10 bg-[#121212] hover:bg-[#1A1A1A]"
              }`}
              data-testid="voltage-72-button"
            >
              <Lightning
                size={28}
                weight="fill"
                className={`sm:w-8 sm:h-8 ${formData.voltage === 72 ? "text-[#00E676]" : "text-[#A1A1AA]"}`}
              />
              <span
                className={`text-lg sm:text-xl font-bold ${formData.voltage === 72 ? "text-[#00E676]" : "text-white"}`}
              >
                72V
              </span>
              <span className="text-[11px] sm:text-xs text-[#A1A1AA]">
                6 baterias
              </span>
            </button>
          </div>
        </div>

        {/* Checklist Template Selection */}
        <div className="space-y-2">
          <label className="text-sm text-[#A1A1AA] ml-1">
            Template de Checklist
          </label>
          <select
            value={formData.checklist_template_id}
            onChange={(e) =>
              setFormData({
                ...formData,
                checklist_template_id: e.target.value,
              })
            }
            className="input-field appearance-none w-full truncate"
            data-testid="checklist-template-select"
          >
            <option value="">Checklist padrão</option>
            {checklistTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name} ({Object.keys(template.items).length} itens)
              </option>
            ))}
          </select>
          <p className="text-[11px] sm:text-xs text-[#A1A1AA] ml-1">
            Selecione um template ou use o checklist padrão
          </p>
        </div>

        {/* Store Selection */}
        <div className="space-y-2">
          <label className="text-sm text-[#A1A1AA] ml-1">Loja / Origem</label>
          <select
            value={formData.store_id}
            onChange={(e) =>
              setFormData({
                ...formData,
                store_id: e.target.value,
                technician_id: "",
              })
            }
            className="input-field appearance-none w-full truncate"
            data-testid="store-select"
          >
            <option value="">Selecione a loja (opcional)</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name} {store.city ? `- ${store.city}` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Technician Selection */}
        <div className="space-y-2">
          <label className="text-sm text-[#A1A1AA] ml-1">
            Técnico Responsável
          </label>
          <select
            value={formData.technician_id}
            onChange={(e) =>
              setFormData({ ...formData, technician_id: e.target.value })
            }
            className="input-field appearance-none w-full truncate"
            data-testid="technician-select"
          >
            <option value="">Selecione o técnico (opcional)</option>
            {technicians.map((tech) => (
              <option key={tech.id} value={tech.id}>
                {tech.name} {tech.store_name ? `(${tech.store_name})` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Info Card */}
        <div className="card-elevated p-4 sm:p-5 mt-2">
          <h3 className="font-bold text-sm sm:text-base mb-3 text-white">
            Informações
          </h3>
          <ul className="text-xs sm:text-sm text-[#A1A1AA] space-y-2 ml-1">
            <li>
              • A moto {formData.voltage}V precisará de{" "}
              {formData.voltage === 60 ? "5" : "6"} baterias.
            </li>
            <li>• O chassi deve ser único no sistema.</li>
            <li>• Após cadastro, vincule as baterias via QR Code.</li>
            {formData.store_id && (
              <li>
                • Loja e técnico ficarão registrados para rastreabilidade.
              </li>
            )}
          </ul>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !formData.chassis_sn || !formData.model}
          className="btn-primary w-full py-4 text-base font-bold mt-4 shadow-lg"
          data-testid="submit-new-moto"
        >
          {loading ? "Cadastrando..." : "Cadastrar Moto"}
        </button>
      </form>
    </Layout>
  );
}
